/**
 * Currency utility for region-based pricing display
 * 
 * Detects user's region and provides appropriate currency formatting
 */

export interface CurrencyInfo {
  code: string;
  symbol: string;
  rate: number;  // Conversion rate from USD
  position: 'before' | 'after';
  locale: string;
}

// Currency configurations (rates are approximate, should be updated via API for production)
export const CURRENCIES: Record<string, CurrencyInfo> = {
  USD: { code: 'USD', symbol: '$', rate: 1, position: 'before', locale: 'en-US' },
  EUR: { code: 'EUR', symbol: '€', rate: 0.92, position: 'before', locale: 'de-DE' },
  GBP: { code: 'GBP', symbol: '£', rate: 0.79, position: 'before', locale: 'en-GB' },
  INR: { code: 'INR', symbol: '₹', rate: 83.5, position: 'before', locale: 'en-IN' },
  CAD: { code: 'CAD', symbol: 'CA$', rate: 1.36, position: 'before', locale: 'en-CA' },
  AUD: { code: 'AUD', symbol: 'A$', rate: 1.53, position: 'before', locale: 'en-AU' },
  JPY: { code: 'JPY', symbol: '¥', rate: 154, position: 'before', locale: 'ja-JP' },
  CNY: { code: 'CNY', symbol: '¥', rate: 7.24, position: 'before', locale: 'zh-CN' },
  BRL: { code: 'BRL', symbol: 'R$', rate: 5.0, position: 'before', locale: 'pt-BR' },
  MXN: { code: 'MXN', symbol: 'MX$', rate: 17.2, position: 'before', locale: 'es-MX' },
  SGD: { code: 'SGD', symbol: 'S$', rate: 1.34, position: 'before', locale: 'en-SG' },
  AED: { code: 'AED', symbol: 'د.إ', rate: 3.67, position: 'after', locale: 'ar-AE' },
  CHF: { code: 'CHF', symbol: 'CHF', rate: 0.88, position: 'before', locale: 'de-CH' },
  SEK: { code: 'SEK', symbol: 'kr', rate: 10.5, position: 'after', locale: 'sv-SE' },
  NOK: { code: 'NOK', symbol: 'kr', rate: 10.8, position: 'after', locale: 'nb-NO' },
  DKK: { code: 'DKK', symbol: 'kr', rate: 6.9, position: 'after', locale: 'da-DK' },
  PLN: { code: 'PLN', symbol: 'zł', rate: 4.0, position: 'after', locale: 'pl-PL' },
  ZAR: { code: 'ZAR', symbol: 'R', rate: 18.7, position: 'before', locale: 'en-ZA' },
  NZD: { code: 'NZD', symbol: 'NZ$', rate: 1.67, position: 'before', locale: 'en-NZ' },
  HKD: { code: 'HKD', symbol: 'HK$', rate: 7.82, position: 'before', locale: 'zh-HK' },
  KRW: { code: 'KRW', symbol: '₩', rate: 1330, position: 'before', locale: 'ko-KR' },
  PHP: { code: 'PHP', symbol: '₱', rate: 56.5, position: 'before', locale: 'en-PH' },
  IDR: { code: 'IDR', symbol: 'Rp', rate: 15800, position: 'before', locale: 'id-ID' },
  THB: { code: 'THB', symbol: '฿', rate: 35.5, position: 'before', locale: 'th-TH' },
  MYR: { code: 'MYR', symbol: 'RM', rate: 4.7, position: 'before', locale: 'ms-MY' },
  VND: { code: 'VND', symbol: '₫', rate: 24500, position: 'after', locale: 'vi-VN' },
  PKR: { code: 'PKR', symbol: '₨', rate: 278, position: 'before', locale: 'ur-PK' },
  BDT: { code: 'BDT', symbol: '৳', rate: 110, position: 'before', locale: 'bn-BD' },
  NGN: { code: 'NGN', symbol: '₦', rate: 1550, position: 'before', locale: 'en-NG' },
  EGP: { code: 'EGP', symbol: 'E£', rate: 49, position: 'before', locale: 'ar-EG' },
  TRY: { code: 'TRY', symbol: '₺', rate: 32, position: 'before', locale: 'tr-TR' },
  RUB: { code: 'RUB', symbol: '₽', rate: 92, position: 'after', locale: 'ru-RU' },
  UAH: { code: 'UAH', symbol: '₴', rate: 41, position: 'before', locale: 'uk-UA' },
  ILS: { code: 'ILS', symbol: '₪', rate: 3.7, position: 'before', locale: 'he-IL' },
  SAR: { code: 'SAR', symbol: 'ر.س', rate: 3.75, position: 'after', locale: 'ar-SA' },
  QAR: { code: 'QAR', symbol: 'ر.ق', rate: 3.64, position: 'after', locale: 'ar-QA' },
  KWD: { code: 'KWD', symbol: 'د.ك', rate: 0.31, position: 'after', locale: 'ar-KW' },
  CLP: { code: 'CLP', symbol: 'CLP$', rate: 960, position: 'before', locale: 'es-CL' },
  COP: { code: 'COP', symbol: 'COL$', rate: 4000, position: 'before', locale: 'es-CO' },
  ARS: { code: 'ARS', symbol: 'AR$', rate: 890, position: 'before', locale: 'es-AR' },
  PEN: { code: 'PEN', symbol: 'S/', rate: 3.75, position: 'before', locale: 'es-PE' },
};

// Country to currency mapping
const COUNTRY_TO_CURRENCY: Record<string, string> = {
  US: 'USD', CA: 'CAD', MX: 'MXN',
  GB: 'GBP', IE: 'EUR', 
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR', PT: 'EUR', GR: 'EUR', FI: 'EUR',
  CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN',
  IN: 'INR', PK: 'PKR', BD: 'BDT',
  JP: 'JPY', CN: 'CNY', KR: 'KRW', HK: 'HKD', TW: 'TWD',
  SG: 'SGD', MY: 'MYR', TH: 'THB', VN: 'VND', PH: 'PHP', ID: 'IDR',
  AU: 'AUD', NZ: 'NZD',
  BR: 'BRL', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN',
  ZA: 'ZAR', NG: 'NGN', EG: 'EGP',
  AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', IL: 'ILS',
  TR: 'TRY', RU: 'RUB', UA: 'UAH',
};

// Timezone to country mapping (fallback)
const TIMEZONE_TO_COUNTRY: Record<string, string> = {
  'America/New_York': 'US', 'America/Los_Angeles': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
  'America/Toronto': 'CA', 'America/Vancouver': 'CA',
  'America/Mexico_City': 'MX',
  'Europe/London': 'GB',
  'Europe/Paris': 'FR', 'Europe/Berlin': 'DE', 'Europe/Rome': 'IT', 'Europe/Madrid': 'ES',
  'Europe/Amsterdam': 'NL', 'Europe/Brussels': 'BE', 'Europe/Vienna': 'AT',
  'Europe/Zurich': 'CH', 'Europe/Stockholm': 'SE', 'Europe/Oslo': 'NO', 'Europe/Copenhagen': 'DK',
  'Europe/Warsaw': 'PL', 'Europe/Moscow': 'RU', 'Europe/Kiev': 'UA', 'Europe/Istanbul': 'TR',
  'Asia/Kolkata': 'IN', 'Asia/Mumbai': 'IN', 'Asia/Karachi': 'PK', 'Asia/Dhaka': 'BD',
  'Asia/Tokyo': 'JP', 'Asia/Shanghai': 'CN', 'Asia/Hong_Kong': 'HK', 'Asia/Seoul': 'KR', 'Asia/Taipei': 'TW',
  'Asia/Singapore': 'SG', 'Asia/Kuala_Lumpur': 'MY', 'Asia/Bangkok': 'TH', 'Asia/Ho_Chi_Minh': 'VN',
  'Asia/Manila': 'PH', 'Asia/Jakarta': 'ID',
  'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA', 'Asia/Qatar': 'QA', 'Asia/Kuwait': 'KW', 'Asia/Jerusalem': 'IL',
  'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Pacific/Auckland': 'NZ',
  'America/Sao_Paulo': 'BR', 'America/Buenos_Aires': 'AR', 'America/Santiago': 'CL', 'America/Bogota': 'CO', 'America/Lima': 'PE',
  'Africa/Johannesburg': 'ZA', 'Africa/Lagos': 'NG', 'Africa/Cairo': 'EG',
};

/**
 * Detect user's country from various sources
 */
export function detectUserCountry(): string {
  // Try timezone first - most reliable indicator of physical location
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const country = TIMEZONE_TO_COUNTRY[timezone];
    if (country) {
      return country;
    }
  } catch (e) {
    console.warn('Could not detect timezone:', e);
  }

  // Fallback to navigator.language region subtag
  const lang = navigator.language || (navigator as any).userLanguage;
  if (lang) {
    const parts = lang.split('-');
    if (parts.length > 1) {
      const country = parts[1].toUpperCase();
      if (COUNTRY_TO_CURRENCY[country]) {
        return country;
      }
    }
  }

  // Default to US
  return 'US';
}

/**
 * Get currency info for a country
 */
export function getCurrencyForCountry(countryCode: string): CurrencyInfo {
  const currencyCode = COUNTRY_TO_CURRENCY[countryCode] || 'USD';
  return CURRENCIES[currencyCode] || CURRENCIES.USD;
}

/**
 * Detect user's currency based on location
 */
export function detectUserCurrency(): CurrencyInfo {
  const country = detectUserCountry();
  return getCurrencyForCountry(country);
}

/**
 * Convert USD price to local currency
 */
export function convertPrice(usdPrice: number, currency: CurrencyInfo): number {
  return Math.round(usdPrice * currency.rate * 100) / 100;
}

/**
 * Format price in local currency
 */
export function formatPrice(
  usdPrice: number, 
  currency: CurrencyInfo,
  options?: { showDecimals?: boolean }
): string {
  const localPrice = convertPrice(usdPrice, currency);
  const { showDecimals = true } = options || {};
  
  // For currencies with high values (JPY, KRW, IDR, etc.), don't show decimals
  const noDecimalCurrencies = ['JPY', 'KRW', 'IDR', 'VND', 'CLP', 'COP', 'PKR', 'NGN', 'ARS', 'RUB'];
  const useDecimals = showDecimals && !noDecimalCurrencies.includes(currency.code);
  
  try {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: useDecimals ? 0 : 0,
      maximumFractionDigits: useDecimals ? 2 : 0,
    }).format(localPrice);
  } catch (e) {
    // Fallback formatting
    const formattedNumber = useDecimals 
      ? localPrice.toFixed(2) 
      : Math.round(localPrice).toString();
    
    if (currency.position === 'before') {
      return `${currency.symbol}${formattedNumber}`;
    } else {
      return `${formattedNumber}${currency.symbol}`;
    }
  }
}

/**
 * Get formatted price with both local and USD
 */
export function formatPriceWithUSD(
  usdPrice: number,
  currency: CurrencyInfo
): { local: string; usd: string; showUSD: boolean } {
  const local = formatPrice(usdPrice, currency);
  const usd = formatPrice(usdPrice, CURRENCIES.USD);
  
  return {
    local,
    usd,
    showUSD: currency.code !== 'USD',
  };
}

// Store user's selected/detected currency
let userCurrency: CurrencyInfo | null = null;

/**
 * Get or detect user's currency (cached)
 */
export function getUserCurrency(): CurrencyInfo {
  if (!userCurrency) {
    // Check localStorage for user preference
    const stored = localStorage.getItem('preferred_currency');
    if (stored && CURRENCIES[stored]) {
      userCurrency = CURRENCIES[stored];
    } else {
      userCurrency = detectUserCurrency();
    }
  }
  return userCurrency;
}

/**
 * Set user's preferred currency
 */
export function setUserCurrency(currencyCode: string): void {
  if (CURRENCIES[currencyCode]) {
    userCurrency = CURRENCIES[currencyCode];
    localStorage.setItem('preferred_currency', currencyCode);
  }
}

/**
 * Get all available currencies for selection
 */
export function getAvailableCurrencies(): CurrencyInfo[] {
  return Object.values(CURRENCIES);
}

export default {
  detectUserCurrency,
  detectUserCountry,
  formatPrice,
  formatPriceWithUSD,
  convertPrice,
  getUserCurrency,
  setUserCurrency,
  getAvailableCurrencies,
  CURRENCIES,
};
